import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getTokens } from '@rsos/sinatra';
import { getAPIHost } from '@rsos/utils/metaTags';
import { transformFeedbackToRequest, type SubmitFeedbackArgs } from './utils';

interface SubmitFeedbackResponse {
  id: number;
  status: string;
  message: string;
  created_at: string;
}

const baseQueryWithErrorHandling = fetchBaseQuery({
  baseUrl: getAPIHost(),
  prepareHeaders: headers => {
    const { token } = getTokens() ?? { token: null };
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    headers.set('Content-Type', 'application/json');
    // Organization is already in the URL path, no need for X-RapidSOS-Org header
    return headers;
  },
});

export const transcriptFeedbackApi = createApi({
  reducerPath: 'transcriptFeedbackApi',
  baseQuery: async (args, api, extraOptions) => {
    const result = await baseQueryWithErrorHandling(args, api, extraOptions);

    if (result.error) {
      if ('status' in result.error) {
        return {
          ...result,
          error: {
            status: result.error.status,
            data: result.error.data || null,
          },
        };
      } else {
        return {
          ...result,
          error: {
            status: 'FETCH_ERROR',
            data: { message: String(result.error) },
          },
        };
      }
    }

    return result;
  },
  endpoints: builder => ({
    submitFeedback: builder.mutation<
      SubmitFeedbackResponse,
      SubmitFeedbackArgs & { orgName?: string }
    >({
      query: args => {
        const orgName = args.orgName || 'default';
        return {
          url: `/v1/capstone/psaps/${orgName}/transcript_feedback`,
          method: 'POST',
          body: transformFeedbackToRequest(args),
        };
      },
    }),
  }),
});

export const { useSubmitFeedbackMutation } = transcriptFeedbackApi;
